// ============================================================================
// AUDIT PARTNER MASTER SCREEN — FSA Signature Workflow
// ============================================================================
// Lists fsa.tbl_audit_partner. Standard CRUD with firm filter dropdown.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useFsaAuditPartners } from 'hooks/api/fsa/useFsaSignature';
import { fsaAuditPartnerService, fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditPartner, AuditPartnerSearchRequest } from 'api/types/fsa/signature.types';
import AuditPartnerFormModal from 'components/modals/modules/fsa/AuditPartnerFormModal';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const FsaAuditPartnerMasterScreen: React.FC = () => {
  const [queryParams, setQueryParams] = useState<AuditPartnerSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaAuditPartners(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [firmFilter, setFirmFilter] = useState<string>('');
  const [partnerNameFilter, setPartnerNameFilter] = useState('');
  const [membershipFilter, setMembershipFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      firmId:           firmFilter || undefined,
      partnerName:      partnerNameFilter || undefined,
      membershipNumber: membershipFilter || undefined,
      statusCode:       statusFilter || undefined,
      includeInactive:  statusFilter === 'INACTIVE',
      page:             1,
    }));
    setFilterOpen(false);
  }, [firmFilter, partnerNameFilter, membershipFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setFirmFilter('');
    setPartnerNameFilter('');
    setMembershipFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      firmId: undefined, partnerName: undefined, membershipNumber: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  // Async firm loader for filter dropdown
  const loadFirmOptions = useCallback(async (search: string) => {
    const resp = await fsaAuditFirmService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined, includeInactive: false });
    return [{ value: '', label: 'All firms' }, ...resp.data.map(f => ({
      value: String(f.id),
      label: `${f.firmName} (${f.firmRegistrationNumber})`,
    }))];
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedPartner, setSelectedPartner] = useState<AuditPartner | null>(null);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns: SyutGridColumn<AuditPartner>[] = useMemo(() => [
    { id: 'firmName', header: 'Firm', accessorKey: 'firmName', enableSorting: true, size: 240 },
    { id: 'partnerName', header: 'Partner Name', accessorKey: 'partnerName', enableSorting: true, size: 220 },
    { id: 'membershipNumber', header: 'Membership No', accessorKey: 'membershipNumber', enableSorting: false, size: 140 },
    { id: 'designation', header: 'Designation', accessorKey: 'designation', enableSorting: true, size: 140 },
    { id: 'frn', header: 'Firm FRN', accessorKey: 'firmRegistrationNumber', enableSorting: false, size: 140 },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100 },
    { id: 'createdAt', header: 'Created',
      accessorFn: (row: AuditPartner) => row.createdAt,
      cell: ({ row }: { row: { original: AuditPartner } }) => <span>{formatDate(row.original.createdAt)}</span>,
      size: 110, enableSorting: true },
  ], []);

  const handleEdit = useCallback((row: AuditPartner) => {
    setSelectedPartner(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: AuditPartner) => {
    setSelectedPartner(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: AuditPartner): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => handleEdit(row) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' },
  ], [handleEdit, handleDeleteClick]);

  const handleAdd = useCallback(() => {
    setSelectedPartner(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add     size={16} />, label: 'Add',     onClick: handleAdd, variant: 'primary' },
    { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => refetch(), variant: 'icon' },
  ], [handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPartner) return;
    setIsDeleting(true);
    try {
      await fsaAuditPartnerService.remove(selectedPartner.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedPartner.partnerName}" deleted successfully.`);
      setSelectedPartner(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedPartner, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Audit Partners"
        description="Per-firm signing partner roster. Used by FSA Signature Workflow to populate signing partners on signed Financial Statements."
        icon={<IconSystem.user size={20} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search partners…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={getRowActions}
              emptyMessage={loading ? 'Loading…' : 'No audit partners found'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Audit Firm"
                  value={firmFilter}
                  onChange={(val) => setFirmFilter((val as string) || '')}
                  isAsync
                  loadOptions={loadFirmOptions}
                  defaultOptions
                  cacheOptions={false}
                  isClearable
                  size="sm"
                  placeholder="All firms"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Partner Name</label>
                <input type="text" className="form-input" value={partnerNameFilter}
                  onChange={(e) => setPartnerNameFilter(e.target.value)}
                  placeholder="Filter by partner name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Membership No</label>
                <input type="text" className="form-input" value={membershipFilter}
                  onChange={(e) => setMembershipFilter(e.target.value)}
                  placeholder="Filter by membership…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete partner "${selectedPartner?.partnerName}"? Existing engagement signature panels that reference this partner will keep their snapshot data, but new engagements won't be able to select them.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <AuditPartnerFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedPartner}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
      />
    </div>
  );
};

export default FsaAuditPartnerMasterScreen;
